import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { stableHash } from "@/lib/link-rotation";
import { getModelsForMake, getRelatedLinks, type ModelWithCount } from "@/lib/vehicle-queries";
import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { maintenanceSpecMakeUrl } from "@/lib/maintenance-spec";
import { RelatedLinks } from "@/components/RelatedLinks";
import { breadcrumbLd, itemListLd, faqLd, SITE_OG_IMAGE, pageTitle } from "@/lib/seo";
import { BrandOverview, buildBrandFaqs } from "@/components/BrandOverview";
import { BrandLogo } from "@/components/BrandLogo";
import { isMainstreamMake, NOINDEX_META } from "@/lib/index-policy";

export const Route = createFileRoute("/$make/")({
  loader: async ({ params }) => {
    const [result, related] = await Promise.all([
      getModelsForMake({ data: { makeSlug: params.make } }),
      getRelatedLinks({ data: { makeSlug: params.make } }),
    ]);
    // Unknown make slugs used to render a 200 shell, which let Google discover
    // an unbounded set of fake indexable URLs. Anything without a real make row
    // is a hard 404 now.
    if (!result.make || result.models.length === 0) {
      throw notFound();
    }
    return { ...result, related };
  },
  component: MakePage,
  head: ({ loaderData, params }) => {
    const makeName = loaderData?.make?.name ?? params.make;
    const path = `https://autospecdata.com/${params.make}`;
    const models = loaderData?.models ?? [];
    return {
      meta: [
        { title: pageTitle(`${makeName} Models — Specs, MPG & Engine Data`) },
        ...(isMainstreamMake(params.make) ? [] : NOINDEX_META),
        {
          name: "description",
          content: `Browse all ${makeName} models with verified fuel economy, engine specifications, and annual fuel cost data. Free, sourced from fueleconomy.gov.`,
        },
        { property: "og:title", content: `${makeName} Models — Specs & MPG | AutoSpecData` },
        {
          property: "og:description",
          content: `Browse all ${makeName} models with verified fuel economy and engine specifications.`,
        },
        { property: "og:url", content: path },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: makeName, path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            itemListLd(
              `${makeName} models`,
              models.map((m: ModelWithCount) => ({
                name: `${makeName} ${m.name}`,
                path: `${path}/${m.slug}`,
              })),
            ),
          ),
        },
        ...(models.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(faqLd(buildBrandFaqs(makeName, models))),
              },
            ]
          : []),
      ],
    };
  },
});

/**
 * Real, per-model facts for the make-hub cards. Previously these pills showed
 * the same three data-type labels on every card regardless of what data the
 * model actually had, which was misleading on 62 hub pages.
 */
function tidyClass(raw: string): string {
  let s = raw
    .replace(/Sport Utility Vehicle/i, "SUV")
    .replace(/\bStandard\b\s*/i, "")
    .replace(/\s*(2WD|4WD|AWD)\b/i, "")
    .replace(/\bCars\b/i, "Car")
    .replace(/\bVans\b/i, "Van")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > 22) s = s.slice(0, 22).trim();
  return s;
}

function modelPills(model: ModelWithCount): string[] {
  const pills: string[] = [];
  if (model.body_class) pills.push(tidyClass(model.body_class));
  if (model.ev_range_best) pills.push(`${Math.round(model.ev_range_best)} mi range`);
  else if (model.mpg_best) pills.push(`Up to ${Math.round(model.mpg_best)} MPG`);
  for (const f of model.fuel_types) {
    if (pills.length >= 3) break;
    if (!pills.some((p) => p.toLowerCase() === f.toLowerCase())) pills.push(f);
  }
  if (pills.length === 0) pills.push(`${model.year_min}–${model.year_max}`);
  return pills.slice(0, 3);
}

/**
 * Deep links per model for the make hub.
 *
 * Models with a large page set already cross-link between their own attribute
 * pages, so they only need the maintenance link (which is never part of the
 * EPA-derived chip row). Thin models — no EPA rows, or only a handful — have
 * one or two attribute pages that used to receive a single inbound link from
 * the model hub, so they get their full set surfaced here.
 */
function modelDeepLinks(model: ModelWithCount): { dt: string; label: string }[] {
  const links: { dt: string; label: string }[] = [
    { dt: "maintenance-schedule", label: "maintenance schedule" },
  ];
  if (model.vehicle_count > 0 && model.vehicle_count <= 6) {
    links.push({ dt: "engine", label: "engine specs" });
    links.push({ dt: "drivetrain", label: "drivetrain" });
    if (model.body_class) links.push({ dt: "vehicle-class", label: "vehicle class" });
    if (model.ev_range_best) links.push({ dt: "ev-range", label: "electric range" });
    else if (model.mpg_best) links.push({ dt: "mpg", label: "MPG" });
  } else if (model.vehicle_count > 0) {
    // Deep models already cross-link their own attribute pages, so one extra
    // entry point is enough — rotated per model slug so coverage spreads across
    // attribute types instead of piling onto the same one every time.
    const pool = [
      { dt: "engine", label: "engine specs" },
      { dt: "drivetrain", label: "drivetrain" },
      ...(model.body_class ? [{ dt: "vehicle-class", label: "vehicle class" }] : []),
      ...(model.ev_range_best
        ? [{ dt: "ev-range", label: "electric range" }]
        : model.mpg_best
          ? [{ dt: "mpg", label: "MPG" }]
          : []),
    ];
    links.push(pool[stableHash(model.slug) % pool.length]!);
  }
  return links;
}

function MakePage() {
  const { make, models, related } = Route.useLoaderData();
  const { make: makeParam } = Route.useParams();

  if (!make) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-foreground">Make not found</h1>
        <p className="mt-2 text-muted-foreground">
          We don't have data for "{makeParam}" yet.
        </p>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">
          Browse all makes
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{make.name}</span>
      </nav>

      {/* Header */}
      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
          <div className="flex items-center gap-4">
            <BrandLogo makeSlug={make.slug} makeName={make.name} size={64} className="rounded-xl bg-card p-2 shadow-soft" />
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              {make.name} Vehicle Models &amp; Specifications
            </h1>
          </div>
          <p className="mt-3 text-lg text-muted-foreground">
            {models.length} {models.length === 1 ? "model" : "models"} with verified
            fuel economy and engine specifications
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <Link
              to="/$make/maintenance-schedule"
              params={{ make: make.slug }}
              className="cta-gold rounded-full"
            >
              {make.name} maintenance schedule by mileage
            </Link>
            <Link
              to="/maintenance-comparison"
              className="rounded-full border border-border bg-card px-4 py-2 font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              Compare intervals across brands
            </Link>
          </div>
        </div>
      </section>

      {/* Models grid */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        {models.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">
              No model data available yet for {make.name}.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {models.map((model: ModelWithCount) => (
              <Link
                key={model.id}
                to="/$make/$model"
                params={{ make: make.slug, model: model.slug }}
                className="model-card"
              >
                <div className="model-card-head">
                  <div>
                    <h2>{model.name}</h2>
                    <p>
                      {model.year_min}–{model.year_max} · {model.vehicle_count}{" "}
                      {model.vehicle_count === 1 ? "variant" : "variants"}
                    </p>
                  </div>
                </div>
                <div className="spec-pills">
                  {modelPills(model).map((p) => (
                    <span key={p}>{p}</span>
                  ))}
                </div>

              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Direct links into each model's data pages — keeps thin models (one or
          two attribute pages) from sitting on a single inbound link. */}
      {models.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-8">
          <h2 className="text-xl font-bold text-foreground">
            {make.name} data pages by model
          </h2>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Jump straight to service intervals and specification data for any {make.name} model.
          </p>
          <ul className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {models.map((model: ModelWithCount) => (
              <li key={`deep-${model.id}`}>
                <span className="font-medium text-foreground">{model.name}</span>{" "}
                {modelDeepLinks(model).map((l, i) => (
                  <span key={l.dt}>
                    {i > 0 && <span className="text-muted-foreground"> · </span>}
                    <Link
                      to="/$make/$model/$dataType"
                      params={{ make: make.slug, model: model.slug, dataType: l.dt }}
                      className="text-muted-foreground hover:text-primary hover:underline"
                    >
                      {l.label}
                    </Link>
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </section>
      )}

      {maintenanceSpecMakeUrl(make.slug) && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-8">
          <MaintenanceSpecLink
            heading={`${make.name} maintenance schedules`}
            body={`For manufacturer-based service intervals and maintenance schedules by model, see MaintenanceSpec.`}
            anchor={`${make.name} maintenance schedules`}
            href={maintenanceSpecMakeUrl(make.slug)!}
          />
        </section>
      )}

      <BrandOverview makeName={make.name} models={models} />

      <RelatedLinks makeName={make.name} makeSlug={make.slug} related={related} />
    </div>
  );
}
